import React from 'react';
import { Tooltip, Button } from 'antd';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import TextTruncate from 'react-text-truncate';
import HtmlParser from 'react-html-parser';

import CustomerInfoColumn from '@/components/customers/CustomerInfoColumn';
import PhoneNumberList from '@/components/PhoneNumberList';
import {
    stripHtml,
    isValidDate,
    renderTelesaleTime,
    renderTelesaleDeadline,
    renderTelesaleFollowStatusTag,
    renderTelesaleAssignmentType
} from '@/utils/helpers';

/**
 * @param {Object} params
 * @param {Object} params.permissions
 * @param {Function} params.onDetail
 * @param {Function} params.openAssignedResult
 * @param {Object} params.authUser
 */
export const telesaleFollowUpColumns = ({
    permissions,
    onDetail,
    openAssignedResult,
    authUser,
    acceptTelesale,
    unacceptTelesale
}) =>
    [
        {
            title: '',
            key: 'action',
            width: 30,
            align: 'center',
            render: (_, record) => (
                <Tooltip title="Xem chi tiết telesale">
                    <EyeOutlined
                        style={{ color: '#1890ff', cursor: 'pointer', fontSize: 16 }}
                        onClick={() => onDetail(record.id)}
                    />
                </Tooltip>
            )
        },
        {
            title: 'Thông tin khách hàng',
            key: 'customer',
            width: 300,
            render: (_, record) => {
                const customer = {
                    id: record?.customer?.id,
                    code: record?.customer?.code,
                    full_name: record?.customer?.full_name,
                    branch_name: record?.customer?.branche?.name,
                    conditions: record?.customer?.conditions,
                    customer_source: record?.customer?.customer_source?.name,
                    customer_status: record?.customer?.customer_status?.name,
                    customer_status_color: record?.customer?.customer_status?.text_color,
                }
                return (
                    <CustomerInfoColumn
                        record={customer}
                        nameField="full_name"          // field tuỳ module
                        idField="id"
                        codeField="code"
                        branchField="branch_name"
                        medicalBookField=""
                        conditionField="conditions"
                        enableLink={true}
                        sourceField="customer_source"
                        statusField="customer_status"
                        statusColorField="customer_status_color"
                        tabs='telesale_tuvan'
                    />
                )
            },
        },
        {
            title: 'Số điện thoại',
            dataIndex: 'phone',
            width: '120px',
            key: 'phone',
            render: (text, record) => {
                return (
                    <PhoneNumberList phones={record.customer?.phones} />
                )
            }
        },
        {
            title: 'Nhân viên tiếp nhận',
            dataIndex: 'accepted_by',
            key: 'accepted_by',
            width: '250px',
            render: (_, record) => {
                const staff = record.accepted_by;
                const isAssignedMode = record.mode === 'assigned';
                const isAccepted = !!staff?.id;
                const isMe = staff?.id === authUser?.id; // authUser từ redux
                const canAccept = isAssignedMode && !isAccepted;
                const canUnaccept = isAssignedMode && isAccepted && isMe;

                return (
                    <div>
                        {/* Đã có người tiếp nhận */}
                        {isAccepted && (
                            <div style={{ marginBottom: 4 }}>
                                <span style={{ color: "#999" }}>
                                    {staff?.user_role?.name || ""}:{' '}
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                    {staff?.full_name}
                                </span>
                            </div>
                        )}

                        {/* Chưa tiếp nhận → Tiếp nhận */}
                        {canAccept && (
                            <Button
                                size="small"
                                type="primary"
                                onClick={() => acceptTelesale(record.id)}
                            >
                                Tiếp nhận
                            </Button>
                        )}

                        {/* Đã tiếp nhận bởi chính mình → Hủy tiếp nhận */}
                        {canUnaccept && (
                            <Button
                                size="small"
                                danger
                                onClick={() => unacceptTelesale(record.id)}
                            >
                                Hủy tiếp nhận
                            </Button>
                        )}
                    </div>
                );
            }
        },

        {
            title: 'Trạng thái trả kết quả',
            width: '180px',
            dataIndex: 'status',
            key: 'status',
            render: (text, record) => {
                const isDone = Number(text) === 1;

                const label = isDone ? 'Đã trả kết quả' : 'Chưa trả kết quả';
                const bgColor = isDone ? 'green' : '#f5b22d';
                const canUpdate = permissions.updateResult;

                const button = (
                    <Button
                        icon={canUpdate ? <EditOutlined /> : null}
                        size="small"
                        style={{
                            background: bgColor,
                            color: '#fff',
                            border: 'none',
                            cursor: canUpdate ? 'pointer' : 'default',
                        }}
                        onClick={
                            canUpdate
                                ? () => openAssignedResult(record.id)
                                : undefined
                        }
                    >
                        {label}
                    </Button>
                );

                return (
                    <div>
                        {canUpdate ? (
                            <Tooltip title="Cập nhật kết quả telesale" placement="right">
                                {button}
                            </Tooltip>
                        ) : (
                            button
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Trạng thái follow',
            dataIndex: 'follow_status',
            key: 'follow_status',
            width: '170px',
            render: (text, record) => renderTelesaleFollowStatusTag(record.follow_status)
        },
        {
            title: 'Hạn telesale',
            dataIndex: 'deadline',
            key: 'deadline',
            width: '170px',
            render: (text, record) => renderTelesaleDeadline(record.deadline, record.status)
        },
        {
            title: 'Thời điểm telesale',
            dataIndex: 'handled_at',
            key: 'handled_at',
            width: '180px',
            render: (text, record) => renderTelesaleTime(record.deadline, record.handled_at)
        },
        {
            title: 'Kết quả telesale',
            dataIndex: 'result_content',
            key: 'result_content',
            width: '250px',
            render: (text, record) => {
                return (
                    <div style={{ wordBreak: "break-word" }}>
                        <Tooltip title={HtmlParser(text)} placement="topLeft">
                            <TextTruncate line={3} truncateText="…" text={stripHtml(text)} />
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            title: 'Dịch vụ quan tâm',
            dataIndex: 'service',
            key: 'service',
            width: '170px',
            render: (text, record) => {
                return (
                    <>{record?.service?.name}</>
                )
            }
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            width: '250px',
            render: (text, record) => {
                return (
                    <div style={{ wordBreak: "break-word" }}>
                        <Tooltip title={HtmlParser(text)} placement="topLeft">
                            <TextTruncate line={1} truncateText="…" text={stripHtml(text)} />
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            title: 'Nhân viên được phân công',
            dataIndex: 'staff',
            key: 'staff',
            width: '250px',
            render: (text, record) => {
                const staffs = record.staff || [];
                return (
                    <>
                        <div>
                            {staffs.map((staff) => (
                                <div key={staff.staff_id}>
                                    <span style={{ color: "#999" }}>
                                        {staff?.user?.user_role?.name || ""}:{' '}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>
                                        {staff?.user?.full_name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )
            }
        },
        {
            title: 'Hình thức telesale',
            dataIndex: 'mode', // hoặc 'mode' tuỳ DB
            key: 'mode',
            width: '160px',
            render: (text, record) => renderTelesaleAssignmentType(
                record.mode
            )
        },
        {
            title: 'Ngày giao telesale',
            dataIndex: 'created_at',
            key: 'created_at',
            width: '200px',
            sorter: true,
            render: (text, record) => {
                const staff = record.created_user;

                return (
                    <>
                        <div>{isValidDate(text, true)}</div>

                        {staff && staff.full_name && (
                            <div>
                                {staff.user_role?.name && (
                                    <span style={{ color: "#999", fontSize: 13.5 }}>
                                        {staff.user_role.name}:{' '}
                                    </span>
                                )}
                                <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                                    {staff.full_name}
                                </span>
                            </div>
                        )}
                    </>
                );
            }
        }
    ];
